import { FormContainer, FormContainerProps } from 'src/UI/dynamicUI/layout/DeprecatedDynamicEntity';
import { Alert, Group } from '@mantine/core';
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import { InlineClickableText } from 'src/common/components/inline-clickable-text/InlineClickableText';
import { useCurrency } from 'src/common/hooks/useCurrency';
import { describeFeedMatch } from 'src/financial/financial-data/feedMatch';
import { describeFeedStaleness } from 'src/financial/financial-data/feedStaleness';
import { useConnectAsset } from 'src/financial/financial-data/hooks/useConnectAsset';
import { useFeedMatch } from 'src/financial/financial-data/hooks/useFeedMatch';
import { useProviderReferenceSharing } from 'src/financial/financial-data/hooks/useProviderReferenceSharing';
import {
  describeProviderReferenceHolders,
  describeSharedReferenceResolution,
} from 'src/financial/financial-data/providerReferenceHolders';
import { useFlags } from 'src/feature-flags/FlagsHook';
import PageLoader from 'src/UI/loader/PageLoader';
import { notifyAndLogError, notifyAndWarn } from 'src/UI/common/ui-helpers';
import { ApiError, ApiErrorCode } from 'src/error/ApiError';
import { useParams } from 'react-router';
import { FinancialItemFormHeader } from 'src/financial/financial-data/financial-item/rework/FinancialItemFormHeader';
import { FeedDisconnectButton } from './FeedDisconnectButton';
import { FeedMatchWarning } from './FeedMatchWarning';

type FinancialFormContainerProps = {
  feedInfo: FeedInfo;
  onConnect?: (id: string) => void;
  hasProviderReference?: boolean;
  /** Entity type, e.g. 'SpendingAccount'. Names what the reworked header's save button saves. */
  product?: string;
  // The form's own name for its provider reference number field, in lower case.
  referenceLabel?: string;
} & FormContainerProps;

export type FeedInfo = {
  isUpdatedAutomatically: boolean;
  hasFeedData: boolean;
  isOpenBanking?: boolean;
  // The date of the data feed delivery that last updated the asset.
  feedUpdatedOn?: string | null;
};

export const FinancialFormContainer = (props: FinancialFormContainerProps) => {
  const {
    feedInfo,
    hasProviderReference = false,
    product,
    referenceLabel = 'provider reference number',
    ...formContainerProps
  } = props;
  const {
    isUpdatedAutomatically,
    hasFeedData,
    isOpenBanking = false,
    feedUpdatedOn = null,
  } = feedInfo;
  const { id: assetId = '', clientId = '' } = useParams<{ id: string; clientId: string }>();
  const { connectAsset, isLoading } = useConnectAsset({ clientId });
  const {
    flagEnabledFinancialItemOwners,
    flagEnabledFinancialRework,
    flagEnabledFeedMatchingRules,
  } = useFlags();
  const { data: providerReferenceSharing } = useProviderReferenceSharing(
    clientId,
    hasProviderReference ? assetId : '',
  );
  const { data: feedMatch } = useFeedMatch(
    clientId,
    hasProviderReference ? assetId : '',
    flagEnabledFeedMatchingRules,
  );
  const canConnectToFeed = flagEnabledFeedMatchingRules
    ? feedMatch?.outcome === 'MATCH'
    : hasFeedData;
  const feedStaleness =
    flagEnabledFeedMatchingRules && isUpdatedAutomatically && !isOpenBanking
      ? describeFeedStaleness(feedUpdatedOn)
      : null;
  const currency = useCurrency();
  // The provider reference sharing warning already covers another client's copy under the same
  // identifiers, so only competition it can't see (same client, policy number alone) gets this
  // one. It doesn't say a connected asset has stopped updating, so a connected asset keeps this one.
  const coveredBySharingWarning =
    feedMatch?.problem === 'COMPETING_ASSET' &&
    !feedMatch.is_connected &&
    !!providerReferenceSharing?.needs_review;
  const feedMatchExplanation =
    flagEnabledFeedMatchingRules && feedMatch && !coveredBySharingWarning
      ? describeFeedMatch(feedMatch, {
          currency,
          ownersEnabled: flagEnabledFinancialItemOwners,
          referenceLabel,
        })
      : null;

  const handleConnectAsset = async () => {
    try {
      await connectAsset(assetId);
      props.onConnect?.(assetId);
    } catch (error) {
      if (ApiError.isApiError(ApiErrorCode.FEED_MATCH_REFUSED, error)) {
        notifyAndWarn('Asset not connected to the data feed', `${error.message}.`);
        return;
      }
      notifyAndLogError(
        'Failed to connect asset to feed',
        'Something went wrong when trying to connect the feed. Please try again and if the problem persists, contact support.',
        error,
      );
    }
  };

  const content = (
    <>
      {isLoading && <PageLoader />}
      {flagEnabledFeedMatchingRules && isUpdatedAutomatically && !isOpenBanking && assetId && (
        <Group justify="flex-end" mb="xxs">
          <FeedDisconnectButton
            clientId={clientId}
            assetId={assetId}
            referenceLabel={referenceLabel}
            onDisconnected={id => props.onConnect?.(id)}
          />
        </Group>
      )}
      {feedStaleness && (
        <Alert
          variant="light"
          color="yellow"
          radius="lg"
          icon={<IconAlertTriangle />}
          mb={'xxs'}
          data-testid="feed-staleness-warning"
        >
          {feedStaleness}
        </Alert>
      )}
      {feedMatchExplanation && <FeedMatchWarning explanation={feedMatchExplanation} />}
      {!isUpdatedAutomatically && canConnectToFeed && (
        <Alert variant="light" color="blue" radius="lg" icon={<IconInfoCircle />} mb={'xxs'}>
          {flagEnabledFeedMatchingRules
            ? 'We found a wrapper in a data feed that matches this asset.'
            : 'We found a data feed that matches this provider reference number.'}{' '}
          To keep this asset updated automatically,{' '}
          <InlineClickableText onClick={handleConnectAsset}>
            connect it to the feed
          </InlineClickableText>
          .
        </Alert>
      )}
      {providerReferenceSharing?.needs_review && (
        <Alert
          variant="light"
          color="yellow"
          radius="lg"
          icon={<IconAlertTriangle />}
          mb={'xxs'}
          data-testid="provider-reference-sharing-banner"
        >
          This provider reference number is also used on an asset for{' '}
          {describeProviderReferenceHolders(providerReferenceSharing.holders)}.{' '}
          {describeSharedReferenceResolution({
            cannotBeJoint: providerReferenceSharing.cannot_be_joint,
            ownersEnabled: flagEnabledFinancialItemOwners,
            feedMatchingRules: flagEnabledFeedMatchingRules,
          })}
        </Alert>
      )}
      {props.children}
    </>
  );

  if (flagEnabledFinancialRework && product) {
    return (
      <FinancialItemFormHeader product={product} title={formContainerProps.title}>
        {content}
      </FinancialItemFormHeader>
    );
  }

  return <FormContainer {...formContainerProps}>{content}</FormContainer>;
};
